import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonSkeletonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { SuperButtonComponent } from '@qid/super';
import { Subject, takeUntil } from 'rxjs';
import { TimePeriod } from '../../../../enum/time-period.enum';
import { activeAccountChanges } from '../../../authentication';
import { CheckoutComponent, CheckoutLineItem } from '../../../order/components/checkout/checkout.component';
import { CreateOrderQuoteDto, OrderQuote } from '../../../order/dto/create-order-quote.dto';
import { OrderPriceBreakup } from '../../../order/interface/order-price.breakup.interface';
import { OrderApi } from '../../../order/services/order.api';
import { Plan, PlanPricingModel, PlanService } from '../../../plan';
import { PlanStatus } from '../../../plan/lib/enum/plan-status.enum';
import { PlanType } from '../../../plan/lib/enum/plan-type.enum';
import { PlanVisibility } from '../../../plan/lib/enum/plan-visibility.enum';
import { ChildOrgActivePlans } from '../../../plan-purchase/interface/child-org-active-plans.interface';
import { PlanPurchase } from '../../../plan-purchase/models/plan-purchase.model';
import { PlanPurchaseService } from '../../../plan-purchase/services/plan-purchase.service';

type Step = 'list' | 'accounts' | 'plans' | 'checkout';

export interface BranchBillingRow {
  childOrg: ChildOrgActivePlans['childOrg'];
  primaryPurchase: PlanPurchase | null;
  selected: boolean;
  selectedPlanId: string | null;
}

interface BulkLineItem {
  organisationId: string;
  organisationName: string;
  plan: Plan;
  pricing: PlanPricingModel;
}

@Component({
  selector: 'app-branch-billing',
  templateUrl: './branch-billing.page.html',
  styleUrls: ['./branch-billing.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SuperButtonComponent,
    CheckoutComponent,
    IonHeader,
    IonToolbar,
    IonBackButton,
    IonButtons,
    IonButton,
    IonIcon,
    IonTitle,
    IonContent,
    IonSkeletonText,
    IonSegment,
    IonSegmentButton,
    IonLabel,
    IonCheckbox,
  ],
})
export class BranchBillingPage {

  public branches: BranchBillingRow[] | null = null;
  public plans: Plan[] = [];
  public step: Step = 'list';
  public paymentTerms: TimePeriod = TimePeriod.YEARLY;
  public checkoutItems: CheckoutLineItem[] = [];
  public isPaymentProcessing = false;
  public quote: OrderQuote | null = null;

  public readonly TimePeriod = TimePeriod;

  private pageClosed$ = new Subject<void>();
  private quoteTimer?: number;
  private quoteSeq = 0;

  constructor(
    private store: Store,
    private planPurchaseService: PlanPurchaseService,
    private planService: PlanService,
    private orderApi: OrderApi,
  ) {}

  ionViewDidEnter() {
    this.pageClosed$ = new Subject<void>();
    activeAccountChanges(this.store)
      .pipe(takeUntil(this.pageClosed$))
      .subscribe(() => this.load());
  }

  ionViewDidLeave() {
    this.pageClosed$.next();
    this.pageClosed$.complete();
    this.clearQuoteTimer();
  }

  get title(): string {
    switch (this.step) {
      case 'accounts':
        return 'Select accounts';
      case 'plans':
        return 'Select plans';
      case 'checkout':
        return 'Complete your purchase';
      default:
        return 'Branch Billing';
    }
  }

  get selectedAccounts(): BranchBillingRow[] {
    return (this.branches || []).filter((branch) => branch.selected);
  }

  get allAccountsSelected(): boolean {
    return !!this.branches?.length && this.branches.every((branch) => branch.selected);
  }

  get plansForPeriod(): Plan[] {
    return this.plans.filter((plan) => !!this.getPricing(plan));
  }

  get selectedItems(): BulkLineItem[] {
    if (!this.plans.length) {
      return [];
    }

    const items: BulkLineItem[] = [];
    for (const branch of this.selectedAccounts) {
      if (!branch.selectedPlanId) {
        continue;
      }
      const plan = this.plans.find((p) => p._id === branch.selectedPlanId);
      const pricing = plan ? this.getPricing(plan) : undefined;
      if (!plan || !pricing) {
        continue;
      }
      items.push({
        organisationId: branch.childOrg._id,
        organisationName: branch.childOrg.name,
        plan,
        pricing,
      });
    }
    return items;
  }

  get priceBreakup(): OrderPriceBreakup | null {
    return this.quote?.priceBreakup || null;
  }

  get canContinueToPayment(): boolean {
    return (
      this.selectedItems.length > 0 &&
      this.selectedItems.length === this.selectedAccounts.length &&
      !!this.quote?.priceBreakup &&
      this.quote.items?.length === this.selectedAccounts.length
    );
  }

  getPricing(plan: Plan): PlanPricingModel | undefined {
    const period = this.paymentTerms?.toLowerCase();
    return plan?.planPricingModels?.find((pricing) => {
      const byPeriod = pricing.timePeriod?.toLowerCase() === period;
      const byName = pricing.name?.toLowerCase() === period;
      return byPeriod || byName;
    });
  }

  planOptionLabel(plan: Plan): string {
    const amount = this.getPricing(plan)?.amount;
    return amount == null ? plan.name : `${plan.name} · ₹${amount}`;
  }

  getSelectedAmount(branch: BranchBillingRow): number | null {
    if (!branch.selectedPlanId) {
      return null;
    }
    const line = this.quote?.items?.find((item) => item.organisation === branch.childOrg._id);
    return line?.priceBreakup?.subtotal ?? null;
  }

  onPeriodChange(period: TimePeriod) {
    this.paymentTerms = period;
    for (const branch of this.selectedAccounts) {
      const plan = this.plans.find((p) => p._id === branch.selectedPlanId);
      if (branch.selectedPlanId && !this.getPricing(plan)) {
        branch.selectedPlanId = null;
      }
    }
    this.scheduleQuote();
  }

  onPlanChange() {
    this.scheduleQuote();
  }

  startSubscription = () => {
    if (!this.branches?.length) {
      throw 'No branches yet';
    }
    for (const branch of this.branches) {
      branch.selected = false;
      branch.selectedPlanId = null;
    }
    this.paymentTerms = TimePeriod.YEARLY;
    this.quote = null;
    this.step = 'accounts';
  };

  onSelectAllChange(event: CustomEvent) {
    const checked = !!event.detail?.checked;
    for (const branch of this.branches || []) {
      branch.selected = checked;
    }
  }

  continueToPlans = () => {
    if (this.selectedAccounts.length < 1) {
      throw 'Select at least one account';
    }
    this.step = 'plans';
    this.scheduleQuote();
  };

  continueToCheckout = () => {
    if (this.selectedAccounts.some((branch) => !branch.selectedPlanId)) {
      throw 'Select a plan for every account';
    }
    this.checkoutItems = this.selectedItems.map((item) => ({
      organisation: item.organisationId,
      organisationName: item.organisationName,
      plan: item.plan,
    }));
    this.isPaymentProcessing = false;
    this.step = 'checkout';
  };

  back = () => {
    if (this.isPaymentProcessing) {
      return;
    }
    if (this.step === 'checkout') {
      this.step = 'plans';
      return;
    }
    if (this.step === 'plans') {
      this.step = 'accounts';
      return;
    }
    this.step = 'list';
  };

  onCheckoutSuccess = () => {
    void this.load();
  };

  private scheduleQuote() {
    this.clearQuoteTimer();
    this.quoteTimer = window.setTimeout(() => {
      void this.refreshQuote();
    }, 300);
  }

  private async refreshQuote() {
    const items: CreateOrderQuoteDto['items'] = this.selectedAccounts
      .filter((branch): branch is BranchBillingRow & { selectedPlanId: string } => !!branch.selectedPlanId)
      .map((branch) => ({
        organisation: branch.childOrg._id,
        plan_id: branch.selectedPlanId,
      }));

    if (!items.length) {
      this.quoteSeq++;
      this.quote = null;
      return;
    }

    const seq = ++this.quoteSeq;
    const res = await this.orderApi.quote({
      period: this.paymentTerms,
      items,
    });
    if (seq !== this.quoteSeq) {
      return;
    }
    this.quote = res.data || null;
  }

  private clearQuoteTimer() {
    if (this.quoteTimer) {
      clearTimeout(this.quoteTimer);
      this.quoteTimer = undefined;
    }
  }

  private async load() {
    this.step = 'list';
    this.isPaymentProcessing = false;
    this.checkoutItems = [];
    this.quote = null;
    this.branches = null;

    const [children, plansRes] = await Promise.all([
      this.planPurchaseService.readActivePlansForChildren(),
      this.planService.readAllPublic(),
    ]);

    this.plans = (plansRes?.data || []).filter(
      (plan) =>
        plan.visibility === PlanVisibility.public &&
        plan.status === PlanStatus.published &&
        plan.type === PlanType.primary
    );

    this.branches = (children || []).map((entry) => ({
      childOrg: entry.childOrg,
      primaryPurchase: (entry.activePlans || []).find(
        (purchase) => purchase.plan?.type === PlanType.primary
      ) ?? null,
      selected: false,
      selectedPlanId: null,
    }));
  }
}
